import React, { MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Organization } from "../common/ObjectTypes";
import NoResults from "../common/NoResults";
import Pagination from "../common/Pagination";
import { Clear_Large, SearchIcon } from "../common/ProjectIcons";
import { page_size } from "../../api/apiUtils";
import { containerClasses, textClasses, tableClasses, buttonClasses, paginationClasses } from "../common/tailwindClassnames";

type OrganizationsListProps = {
    organizations: Array<Organization>,
    onChangeSearchString: Function,
    onSelectOrg: Function,
    onAddOrg: MouseEventHandler,
    superuser: boolean,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    searchString: string,
    onSearch: Function
}

const OrganizationsList = ({
    organizations,
    onChangeSearchString,
    onSelectOrg,
    onAddOrg,
    superuser,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    searchString,
    onSearch
}: OrganizationsListProps) => {
    const { t } = useTranslation();
    return (
        <div className={containerClasses.page}>
            <div className="mb-3 flex flex-row justify-between items-end">
                <div className="flex flex-col">
                    <h1 data-testid="organizations_page_header" className={textClasses.heading}>{t("Workspaces")}</h1>
                    <div className="flex flex-row items-center">
                        <div className="relative">
                            <input
                                id="name"
                                placeholder={t("Search by name") || ""}
                                className="my-2 p-1.5 border border-neutral-gray-200 dark:border-secondary-gray rounded rounded-sm px-2 text-off-black dark:text-white dark:bg-neutral-gray-700 transition-all duration-300"
                                value={searchString}
                                onChange={(e) => { onChangeSearchString(e) }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        onSearch(searchString);
                                    }
                                }}>
                            </input>
                            {searchString && (
                                <button
                                    className="absolute right-0 top-0 mt-3.5 mr-1 text-dark-green dark:text-dark-green-100 transition-colors duration-300"
                                    onClick={() => {
                                        onChangeSearchString({ target: { value: '' } })
                                        onSearch()
                                    }}
                                >
                                    <Clear_Large />
                                </button>
                            )}
                        </div>
                        <button className="mx-2 bg-dark-green hover:bg-status-green text-white rounded text-sm p-2 h-[34px] self-center transition-colors duration-300"
                            type="button"
                            onClick={() => onSearch()}
                        >
                            <SearchIcon />
                        </button>
                    </div>
                </div>
                {superuser && <button className={buttonClasses.primary}
                    onClick={onAddOrg}>
                    {t("Create Workspace")}
                </button>}
            </div>
            <div className="flex flex-col w-full">
                {organizations.length < 1 &&
                    <NoResults
                        headerMsg={t("No Results Found")}
                        labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                    />}

                {organizations.length >= 1 &&
                    <table className={tableClasses.container + " table-fixed"}>

                        <thead className={tableClasses.header}>
                            <tr>
                                <th className={tableClasses.headerCell + " w-1/3"}>
                                    {t("Name")}
                                </th>
                                <th className={tableClasses.headerCell}>
                                    {t("Description")}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="text-sm dark:text-neutral-gray-400 transition-colors duration-300">
                            {organizations.map((org: Organization) => {
                                return (
                                    <tr key={org.id}
                                        className="border border-gray-lines dark:border-secondary-gray hover:bg-light-blue dark:hover:bg-neutral-gray-700 bg-white dark:bg-off-black transition-colors duration-300"
                                        onClick={() => onSelectOrg(org.id)}
                                    >
                                        <td className="p-3 w-1/3 dark:text-white transition-colors duration-300 cursor-pointer">
                                            {org.name}
                                        </td>
                                        <td className="p-3 max-w-2xl dark:text-white transition-colors duration-300">
                                            <p className="max-w-2xl">{org.description}</p>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>}
                {totalCount > page_size && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container + " dark:text-neutral-gray-400"}
                />}
            </div>
        </div>
    );
};

export default OrganizationsList;